/* eslint-disable jsx-a11y/media-has-caption */
import { Button } from '@insify/design-system/components';
import { headingMd, textMd, textSm } from '@insify/design-system/typography';
import { TrackedDiv } from '@objectiv/tracker-react';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAssistantChat } from '../../../api/getChat';
import { useProfile } from '../../../api/queries/useProfile';
import { useChatStore } from '../../../stores/chatStore';

import styles from './styles.module.css';

import type React from 'react';

interface Props {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const showVideo = true;

const steps = [
  {
    title: 'Maak kennis met Sophie.',
    description:
      'Sophie is je Insify AI assistent. Zij zorgt ervoor dat je je nooit meer zorgen hoeft te maken over verzekeringen. Of dat nu is voor je onderneming of voor jezelf. Sommigen noemen haar onze enige echte Insify product genius.',
    button: `Begrepen`,
  },
];

export const OnboardingModal: React.FC<Props> = ({ isOpen, onOpen, onClose }) => {
  const { t, i18n } = useTranslation('assistant');
  // const drawerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { mutate: sendMessage, isPending, isSuccess } = useAssistantChat();
  const { data: profile } = useProfile();
  const setIsOnboarded = useChatStore.use.setOnboarded();

  const navigate = useNavigate();

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      navigate('/');
    }
  };

  useEffect(() => {
    if (isSuccess) {
      setIsOnboarded();
    }
  }, [isSuccess, setIsOnboarded]);

  useEffect(() => {
    onOpen();
    return onClose;
  }, []);

  // useEffect(() => {
  //   if (videoRef.current && step === 2) {
  //     videoRef.current.currentTime = 13;
  //   }
  // }, [step]);

  return ReactDOM.createPortal(
    // accessibility: inert is used to prevent screen readers from reading the content behind the drawer
    // when the drawer is open. typescript doesn't have support for inert attribute yet, hence the workaround
    <TrackedDiv
      objectiv={{ id: 'chat-onboarding-dialog' }}
      className={`${styles.dialogContainer} ${isOpen ? styles.open : ''}`}
      {...{ inert: !isOpen ? '' : undefined }}
    >
      <div
        className={styles.overlay}
        ref={overlayRef}
        onClick={handleOverlayClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleOverlayClick(e as unknown as React.MouseEvent);
          }
        }}
        tabIndex={0}
        role='button'
        aria-label='Close menu'
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, delay: 0.1 }}
        style={{ pointerEvents: 'auto' }}
        onClick={close}
        id='chat-onboarding-dialog'
        role='dialog'
        className={styles.dialog}
        // ref={drawerRef}
      >
        <div className={styles.content}>
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 80, damping: 15, delay: 0.2 }}
            className={styles.videoWrapper}
            style={{
              borderRadius: '50%',
              overflow: 'hidden',
              width: 100,
              height: 100,
              margin: '0 auto',
              boxShadow: '0 2px 16px rgba(0,0,0,0.12)',
              border: '2px solid #000',
              background: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Add antialiasing for the border
              WebkitMaskImage: 'radial-gradient(circle, white 99%, transparent 100%)',
              maskImage: 'radial-gradient(circle, white 99%, transparent 100%)',
              // Optional: force GPU acceleration for smoother edges
              willChange: 'transform',
            }}
          >
            {showVideo && (
              <video
                src='https://media.insify.com/myinsify/assistant/ai-carmi.webm'
                autoPlay
                playsInline
                controls={false}
                muted={true}
                loop={false}
                disableRemotePlayback
                ref={videoRef}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  display: 'block',
                }}
              />
            )}
          </motion.div>
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 120, damping: 20 }}
            className={styles.textBlock}
          >
            <div className={headingMd}>{steps[0].title}</div>
            <div className={textMd}>{steps[0].description}</div>
          </motion.div>
          <Button
            type='button'
            kind='primary'
            trackingId='start-chat'
            loading={isPending}
            onClick={() => {
              sendMessage({
                message: profile?.result
                  ? `Hoi, ik ben ${profile?.result?.greetingName}.`
                  : 'Hoi, ik ben een onbekende gebruiker.',
              });
            }}
            className={styles.menuItem}
          >
            {isPending ? 'Loading...' : steps[0].button}
          </Button>
          {i18n.language !== 'nl' && (
            <span className={clsx(textSm, styles.disclaimer)}>{t('chat_availability_disclaimer')}</span>
          )}
        </div>
      </motion.div>
    </TrackedDiv>,
    document.body,
  );
};
